import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import Template from "../../common/template/template";
import Map from "./components/map/Map";
import PageBody from "./common/PageBody";
import Header from "./common/Header";
import SignInModal from "./components/signIn/SignInModal";
import JobNeedTimeDescription from "./components/jobNeedTimeDescription/JobNeedTimeDescription";

import {
    fetchJobRatePreHour,
    fetchPostcodeAddressDetails,
    updateStoredJobDetails,
} from "./service";

import {
    getBookAgainDetails,
    getNewJobDetails,
    getSettings,
    removeNewJobDetails,
    setNewJobDetails,
} from "../../storage";

import { showNotification } from "../../common/notifications/notifications";
import useCustomNavigate from "../../common/hooks/useCustomNavigate";
import useIsMobile from "../../common/hooks/useIsMobile";

const defaultRange = [3, 5];

const CheckPrice = ({ isHome = false, isCheckingPrice = false }) => {
    const [searchParams] = useSearchParams();
    const retryJob = Number(searchParams.get("retryJob"));
    const checkPrice = searchParams.get("checkPrice") === "yes";
    const bookAgain = Number(searchParams.get("bookAgain"));

    const isMobile = useIsMobile();

    const settings = getSettings();
    const stepFlowIsEnable = settings?.customer_web_job_step_flow_is_enable;

    const storedJobDetails = useMemo(() => {
        if (bookAgain > 0) return getBookAgainDetails();
        if (retryJob || checkPrice) return getNewJobDetails();

        return null;
    }, [bookAgain, retryJob, checkPrice]);

    const today = useMemo(() => Date.now(), []);
    const isFirstRender = useRef(true);

    const isHaveRetryJob = retryJob > 0;

    const currentStep = useMemo(() => {
        if (isHaveRetryJob) return 4;
        if (checkPrice) return 5;
        if (bookAgain > 0) return 1;

        return 1;
    }, [checkPrice, isHaveRetryJob, bookAgain]);
    
    const [step, setStep] = useState(currentStep);
    const [address, setAddress] = useState(storedJobDetails?.address || null);
    const [jobGeolocationDetails, setJobGeolocationDetails] = useState(storedJobDetails?.jobGeolocationDetails || null);
    const [range, setRange] = useState(storedJobDetails?.range || defaultRange);
    const [skill, setSkill] = useState(storedJobDetails?.skill || null);
    const [subSkill, setSubSkill] = useState(storedJobDetails?.subSkill || null);
    const [jobRatePreHour, setJobRatePreHour] = useState(null);
    const [showSignInModal, setShowSignInModal] = useState(false);
    const [showAddressSearch, setShowAddressSearch] = useState(!storedJobDetails?.jobGeolocationDetails);
    const [jobNotes, setJobNotes] = useState(storedJobDetails?.jobNotes || "");

    const { navigateToCustom } = useCustomNavigate();

    const handleShowSignInModal = useCallback(() => {
        setShowSignInModal(true);
    }, []);

    const handleCloseSignInModal = useCallback(() => {
        setShowSignInModal(false);
    }, []);

    const calculateFutureTime = useCallback(
        (hours) => {
            return hours === 1
                ? today
                : today + (hours - 1) * 60 * 60 * 1000;
        },
        [today]
    );

    const getPostcodeAddressDetails = useCallback(async () => {
        if (!address?.label) return;

        try {
            const res = await fetchPostcodeAddressDetails(address.label);

            if (res?.status === "Success") {
                setJobGeolocationDetails(res.details);
                setShowAddressSearch(false);

                updateStoredJobDetails("jobGeolocationDetails", res.details);
            }
        } catch (error) {
            console.error(
                "getPostcodeAddressDetails error:",
                error
            );
        }
    }, [address]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        getPostcodeAddressDetails();
    }, [getPostcodeAddressDetails]);

    useEffect(() => {
        if (!subSkill?.sub_skill_id) return;

        const getJobRate = async () => {
            try {
                const res = await fetchJobRatePreHour({
                    sub_skill_id: subSkill.sub_skill_id,
                    eta_start_time: calculateFutureTime(range[0]),
                    eta_end_time: calculateFutureTime(range[1]),
                });

                if (res?.status === "Success") {
                    setJobRatePreHour(res.data);
                }
            } catch (error) {
                console.error(
                    "getJobRatePreHour error:",
                    error
                );
            }
        };

        getJobRate();
    }, [subSkill, range, calculateFutureTime]);

    const handleReset = useCallback(() => {
        setAddress(null);
        setJobGeolocationDetails(null);
        setRange(defaultRange);
        setSkill(null);
        setSubSkill(null);
        setJobRatePreHour(null);
        setShowAddressSearch(true);

        removeNewJobDetails();
    }, []);

    const confirmBooking = useCallback(() => {
        setNewJobDetails({
            address,
            jobGeolocationDetails,
            range,
            skill,
            subSkill,
            jobRatePreHour,
            jobNotes
        });

        showNotification({
            notificationType: "CREATE_NEW_ORDER",
            isConfirmed: handleShowSignInModal,
            notConfirmed: () => navigateToCustom("/sign/up?checkPrice=yes"),
        });
    }, [
        address,
        jobGeolocationDetails,
        range,
        skill,
        subSkill,
        jobRatePreHour,
        jobNotes,
        handleShowSignInModal,
        navigateToCustom,
    ]);

    const pageBodyProps = {
        address,
        setAddress,
        jobGeolocationDetails,
        setJobGeolocationDetails,
        range,
        defaultRange,
        setRange,
        skill,
        setSkill,
        subSkill,
        setSubSkill,
        calculateFutureTime,
        jobRatePreHour,
        handleReset,
        confirmBooking,
        showAddressSearch,
        retryJob,
        step,
        setStep,
        jobNotes,
        setJobNotes,
        isCheckingPrice,
        bookAgain,
        ...(isHome && { isHome: true }),
    };

    const shouldShowDescription =  subSkill?.sub_skill_id;
    const mapColumnSize = !stepFlowIsEnable || (stepFlowIsEnable && subSkill?.sub_skill_id) ? "6" : "9";

    const renderContent = () => (
        <>
            {shouldShowDescription && (
                <div className="col-xl-3">
                    <JobNeedTimeDescription
                        sub_skill_id={subSkill.sub_skill_id}
                        isHome={true}
                        stepFlowIsEnable={stepFlowIsEnable}
                    />
                </div>
            )}

            <div className={`col-xl-${mapColumnSize}`}>
                <Map
                    jobLocation={jobGeolocationDetails}
                    isHome={true}
                    step={step}
                />
            </div>

            <div className={`col-xl-${stepFlowIsEnable ? "3" : "6"} ${isMobile && "mt-4"} `}>
                <PageBody {...pageBodyProps} />
            </div>
        </>
    );

    if (isHome) {
        return (
            <div className="row mt-3">
                {renderContent()}
            </div>
        );
    }

    return (
        <Template is_current_job_page>
            <Header />
            <div className={`row mt-3 ${isMobile ? "mx-2" : "mx-5"}`}>
                {renderContent()}
            </div>

            <SignInModal show={showSignInModal} closeModal={handleCloseSignInModal} />
        </Template>
    );
};

export default CheckPrice;